"use client";

import { IconArrowBarLeft } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { logoUrl } from "@/features/media/mediaUrl";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useSidebar } from "@/hooks/useSidebar";

/**
 * The sidebar's header: the tenant's logos and the collapse control.
 *
 * Every configured logo is shown, each linking to its own destination and
 * separated by a pipe — tenants routinely carry several (the city, the operator,
 * a funder). The strip scrolls horizontally when they don't fit, with the
 * scrollbar hidden because one drawn across branding reads as a defect.
 */
export function SidebarHeader() {
  const t = useTranslations();
  const configs = useProjectConfigs();
  const { toggle } = useSidebar();
  const { domain } = useMediaContext();

  const { appLogos, sidebarCollapse } = configs;

  return (
    <header className="flex h-[80px] justify-between pb-4 pl-5 pr-5 pt-4">
      <div className="logos flex h-full justify-start">
        {appLogos?.map((logo, index) => {
          const src = logoUrl(logo.image?.fileName, domain);

          return (
            <a
              className="focus-element flex h-full flex-row items-center"
              href={logo.url}
              target="_blank"
              rel="noreferrer"
              key={`logo-${index}`}
              aria-label={t("logo")}
            >
              {src && (
                // Tenant-uploaded, arbitrary dimensions, same-origin — see the
                // note in Sidebar.tsx.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  className="no-max-width"
                  src={src}
                  alt=""
                  aria-hidden="true"
                />
              )}
              <div className="pipe" />
            </a>
          );
        })}
      </div>

      {sidebarCollapse ? (
        <button
          type="button"
          className="flex cursor-pointer items-center justify-end gap-2 text-sm text-neutral-800 focus-visible:outline focus-visible:outline-blue-500 dark:text-off-white"
          onClick={toggle}
        >
          <IconArrowBarLeft className="text-neutral-500" aria-hidden="true" />
          {t("sidebar.collapse")}
        </button>
      ) : null}
    </header>
  );
}
